import { IndustryModeEnum } from "../api/constants";
import { RBAdminEmail, roleNameEnum } from "./Common";

export const RBPayPhaseZeroConfig = {
  allowRBPayWorker: true,
  isRBPaySetupComplete: false,
  setupEmailSubject: "Setup RB Pay Account Email to Network Admin",
};

// Backend payment-method value for RB Pay. Like Stripe, RB Pay enablement is
// stored as membership in pharmacist.acceptedPaymentMethods (the string "rb_pay"
// present = enabled), NOT as a separate boolean. This is the canonical enum value
// (matches RB-Portal's locationRbPayroll payment type).
export const RB_PAY_PAYMENT_METHOD = "rb_pay";

export const isRBPayPaymentMethod = (method?: string | null) =>
  String(method || "").trim().toLowerCase() === RB_PAY_PAYMENT_METHOD;

// Read: is RB Pay currently enabled for this worker (i.e. "rb_pay" is in the
// accepted payment methods array)?
export const isRBPayEnabledForAccount = (account?: any) => {
  const methods = account?.pharmacist?.acceptedPaymentMethods;
  return Array.isArray(methods) && methods.some(isRBPayPaymentMethod);
};

// ---------------------------------------------------------------------------
// RB Pay status — single source of truth.
// Migration `rbPayApproved` (boolean) -> `rbPayStatus` / `rb_pay_status` (enum).
// Canonical FE tokens, a tolerant normalizer, and a dual-read resolver that
// PREFERS the backend status string and FALLS BACK to the legacy `rbPayApproved`
// boolean while the backend rolls the new field out across every surface. Once
// every surface returns `rbPayStatus` and the boolean is retired, the fallback
// branch in `resolveRbPayStatus` can be removed.
// ---------------------------------------------------------------------------
export const RBPayStatus = {
  ACTIVE: "active",       // Verified / eligible — behaviour unchanged
  PENDING: "pending",     // "Pending Review" — behaviour unchanged
  ON_HOLD: "on_hold",     // NEW — treated exactly as "RB Pay NOT active"
  NOT_SETUP: "not_setup", // absence / NULL — "Not Started"
} as const;

export type RBPayStatusValue = typeof RBPayStatus[keyof typeof RBPayStatus];

// Normalize any backend rb_pay_status representation (casing / separators / known
// synonyms) to a canonical token. Unknown / empty / null -> NOT_SETUP.
export const normalizeRbPayStatus = (value?: string | null): RBPayStatusValue => {
  const token = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (token === "active") return RBPayStatus.ACTIVE;
  if (token === "pending" || token === "pending_review" || token === "in_review") {
    return RBPayStatus.PENDING;
  }
  if (token === "on_hold" || token === "hold" || token === "onhold") {
    return RBPayStatus.ON_HOLD;
  }
  return RBPayStatus.NOT_SETUP;
};

// Resolve an entity's (pharmacist / pharmacy / profile) RB Pay status. Prefers the
// new `rbPayStatus` (a.k.a. `rb_pay_status`) field; when absent, falls back to the
// legacy `rbPayApproved` boolean (true -> ACTIVE). A false/missing boolean cannot
// distinguish pending/hold/not-setup, so it resolves to NOT_SETUP — which reads as
// "not verified / not active" for every gate and badge, preserving today's boolean
// behaviour exactly during the transition.
export const resolveRbPayStatus = (entity?: any): RBPayStatusValue => {
  const raw = entity?.rbPayStatus ?? entity?.rb_pay_status;
  if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
    return normalizeRbPayStatus(raw);
  }
  return entity?.rbPayApproved === true ? RBPayStatus.ACTIVE : RBPayStatus.NOT_SETUP;
};

// Write helper: return a copy of the worker's accepted payment methods with
// "rb_pay" added (enable) or removed (disable), preserving every other method
// (cheque/direct_deposit/emt/stripe/other). Used by the RB Pay toggle so it never
// clobbers the Stripe/direct selections, and vice versa.
export const buildAcceptedMethodsWithRBPay = (account: any, enabled: boolean): string[] => {
  const current = Array.isArray(account?.pharmacist?.acceptedPaymentMethods)
    ? account.pharmacist.acceptedPaymentMethods
    : [];
  const withoutRBPay = current.filter((method: string) => !isRBPayPaymentMethod(method));
  return enabled ? [...withoutRBPay, RB_PAY_PAYMENT_METHOD] : withoutRBPay;
};

interface RBPayEligibilityProps {
  industryMode?: string | null;
  roleName?: string | null;
  account?: any;
}

// RB Pay availability is driven ONLY by the per-entity admin flag
// (worker: account.pharmacist.isRbPayAvailable, location: pharmacy.isRbPayAvailable).
// The network-level flags (network.isRbPayAvailableForWorker/ForLocation) are
// reference-only and intentionally NOT checked here — an admin turns RB Pay on/off
// for each worker and each location individually. Strict `=== true` so false/null/
// missing → not available (safe default: hidden until the admin enables it).
export const isRBPayAvailableForWorker = ({
  industryMode,
  roleName,
  account,
}: RBPayEligibilityProps) => {
  const isSupportedIndustry =
    industryMode === IndustryModeEnum.Dentistry ||
    industryMode === IndustryModeEnum.Pharmaceutical;

  return (
    account?.pharmacist?.isRbPayAvailable === true &&
    isSupportedIndustry &&
    roleName === roleNameEnum.pharmacist
  );
};

// Location availability = the per-location admin flag only (network flag is
// reference-only). Takes the pharmacy/location object directly.
export const isRBPayAvailableForLocation = (location?: any) =>
  location?.isRbPayAvailable === true;

// Is the worker's own RB Pay onboarding active? Source of truth: pharmacist status
// (`rbPayStatus`), with the legacy `rbPayApproved` boolean as a transitional fallback.
export const isRBPayActiveStatus = (account?: any) =>
  resolveRbPayStatus(account?.pharmacist) === RBPayStatus.ACTIVE;

// Worker statuses that earn the owner-facing "RB Pay Verified" badge: 'active' AND
// 'pending' (a.k.a. pending_review). Deliberately the same allow-list as the LOCATION badge
// (RB_PAY_LOCATION_GATING_STATUSES) and the apply gate (RB_PAY_APPLY_ALLOWED_STATUSES) —
// one meaning of "on RB Pay" across worker and location, rather than three. A pending worker
// is assumed likely-approved by payout time, and the RB Pay payout process re-checks that
// both parties are 'active' before it processes anything.
//
// Kept as its OWN list rather than reusing the apply-gate constant: the two rules answer
// different questions ("may this worker apply?" vs "what do we tell the owner?") and must
// stay independently tunable, even though they currently hold the same values.
export const RB_PAY_WORKER_VERIFIED_STATUSES: RBPayStatusValue[] = [
  RBPayStatus.ACTIVE,
  RBPayStatus.PENDING,
];

// A WORKER/applicant is "RB Pay Verified" when the admin has RB Pay switched ON for them AND
// their own RB Pay onboarding is at least submitted. Used by the Owner's Shift Applicants and
// Location Applicants card badge.
//
// Both clauses are required, and they answer different questions:
//   * pharmacist.isRbPayAvailable — the admin master switch. Strict `=== true`, so
//     false / null / missing reads as "not available". Checked independently of the status
//     because nothing in the backend resets rb_pay_status when an admin unticks "Is RB Pay
//     Available": the Admin Portal's worker save writes only is_rb_pay_available, while
//     rb_pay_status is owned by the separate RB Pay profile endpoint. A revoked worker
//     therefore keeps rbPayStatus='active' indefinitely. Without this clause the owner is
//     shown a green "RB Pay Verified" chip for a worker whom evaluateRBPayApplyGate then
//     blocks at the Apply CTA with "contact your network admin" — that gate checks the
//     same flag first, and for the same reason: the flag is the master control.
//   * rbPayStatus — how far their onboarding has actually got (dual-read via
//     resolveRbPayStatus). ON HOLD and not-setup read as not-verified; 'on_hold' in
//     particular must stay excluded, since it means an admin actively suspended them.
//
// An earlier note here said isRbPayAvailable "must NOT be used" because it stays true while
// a worker is pending or on hold. The ON HOLD half of that still matters — which is why the
// status clause is kept — but it never justified ignoring the flag when it is FALSE.
export const isWorkerRBPayVerified = (applicant?: any) =>
  applicant?.pharmacist?.isRbPayAvailable === true &&
  RB_PAY_WORKER_VERIFIED_STATUSES.includes(resolveRbPayStatus(applicant?.pharmacist));

// A LOCATION is "on RB Pay" when its RB Pay setup is at least submitted ('active' OR
// 'pending_review') AND its payment type is actually set to RB Pay. This is a verbatim
// mirror of the backend's RbPayEligibilityService.isEligible
// (src/app/rb-pay/services/rb-pay-eligibility.service.ts:41) — the single rule the server
// uses both to reject an apply (403 ShiftRbPaySetupRequiredException) and to route the
// payout at completion.
//
// US6559 Phase 2 — this is THE ONLY location predicate. It drives, from one place:
//   * the "Shift Payment" indicator (true -> RB Pay Verified, false -> Location Managed)
//   * the standalone "RB Pay Verified" badge kept on network-paid cards
//   * the Phase 1 worker apply gate (only true locations gate at all)
//
// It replaced an earlier `isLocationRBPayVerified` (rbPayStatus === 'active' only, payment
// type ignored) that used to gate the badge. Keeping two rules for one green chip meant the
// same location could render inconsistently: 'active' + 'invoice' showed the badge on a
// network-paid card while the field correctly read "Location Managed" everywhere else — a
// claim the backend's isEligible contradicts. One rule, one meaning.
//
// paymentType is the primary driver (client/tech-lead decision). The status clause is kept
// as a guard because the backend does NOT yet enforce the two fields in lockstep:
// LocationRbPayProfileService writes payment_type and rb_pay_status in independent,
// unguarded statements, and no code path clears payment_type when an admin sets a location
// On Hold. Without the status clause a suspended location (rb_pay_status='on_hold',
// payment_type still 'rb_pay') would be labelled "RB Pay" while the server routes the shift
// to direct_pay. A planned BE change will make status and payment_type consistent
// (active/pending => 'rb_pay', on_hold/null => reset to the default); once it ships this
// clause becomes a no-op and can be dropped, but it must never render differently.
export const RB_PAY_LOCATION_GATING_STATUSES: RBPayStatusValue[] = [
  RBPayStatus.ACTIVE,
  RBPayStatus.PENDING,
];

export const isLocationOnRBPay = (pharmacy?: any) =>
  isRBPayPaymentMethod(pharmacy?.paymentType) &&
  RB_PAY_LOCATION_GATING_STATUSES.includes(resolveRbPayStatus(pharmacy));

// ---------------------------------------------------------------------------
// US6559 Phase 2 — "Shift Payment" indicator.
// ---------------------------------------------------------------------------

// Mirrors the backend's ShiftPaymentRoutingMethod enum
// (src/app/shift/enums/shift-payment-routing-method.enum.ts).
export const ShiftPaymentMethod = {
  RB_PAY: "rbpay",
  NETWORK_PAID: "network_paid",
  DIRECT_PAY: "direct_pay",
} as const;

export type ShiftPaymentMethodValue =
  typeof ShiftPaymentMethod[keyof typeof ShiftPaymentMethod];

export interface ShiftPaymentIndicator {
  showField: boolean;
  value: ShiftPaymentMethodValue | null;
  isRBPay: boolean;
  showLegacyBadge: boolean;
  showNote: boolean;
}

// Is the network (rather than the location) paying the worker for this shift? RB Pay is
// structurally irrelevant when it is — the backend's resolveShiftPaymentMethod short-circuits
// to 'network_paid' before looking at any RB Pay field.
//
// Reads the LOCATION's flag only (`pharmacy.networkPaid`), deliberately ignoring the per-shift
// `shift.networkPaid` column. The backend's billing code consults the shift column first, but
// that column is only written when a shift's completion is acknowledged (ShiftService.update)
// and was added with no default and no backfill (migration 0020) — so on the pre-apply screens
// this indicator serves it is unset or carries whatever was passed to CreateShiftDto, neither of
// which describes how the worker will actually be paid. The location flag is the value the
// business reasons about, and is what the client means by "Network Paid".
export const resolveShiftNetworkPaid = (shift?: any): boolean =>
  shift?.pharmacy?.networkPaid === true;

// Should the "this office is not setup for RB Pay, they will pay you directly..." note be
// shown? Client rule (build spec pg 2/3), all three clauses required:
//   1. the shift is NOT network paid,
//   2. the location is NOT on RB Pay, and
//   3. the NETWORK has RB Pay switched on for locations.
// Clause 3 is a deliberate, spec-mandated exception to the usual rule that network-level RB
// Pay flags are reference-only: the note only makes sense ("this office hasn't set it up") in
// a network where offices actually could. Fails closed when the network is missing.
export const shouldShowRBPayNotSetupNote = (shift?: any): boolean =>
  !resolveShiftNetworkPaid(shift) &&
  !isLocationOnRBPay(shift?.pharmacy) &&
  shift?.pharmacy?.network?.isRbPayAvailableForLocation === true;

// The single entry point every Shift Payment surface uses. Never re-derive these rules in a
// page.
//
// Returns:
//   showField       - render the "Shift Payment" row at all?
//   value           - ShiftPaymentMethod.RB_PAY | DIRECT_PAY, else null
//   isRBPay         - convenience: value === RB_PAY (green badge vs grey chip)
//   showLegacyBadge - network-paid shifts keep TODAY's standalone "RB Pay Verified" chip
//                     instead of the new row (client: "for all other scenarios the current
//                     behaviour should continue unchanged"). Only ever true when the shift is
//                     network paid; the two are mutually exclusive.
//   showNote        - see shouldShowRBPayNotSetupNote. The CALLER still decides placement —
//                     the note belongs on Shift Details / Apply only, never on cards or on
//                     future/confirmed shifts.
//
// `shift.shiftPaymentMethod` is honoured first when present: once a shift's completion is
// acknowledged the backend locks the ACTUAL routing onto the shift, and a completed shift must
// show what really happened rather than a value recomputed from the location's current state.
// It is null on every pre-completion surface (and not yet serialised at all), so the derivation
// below is what runs today.
export const resolveShiftPaymentIndicator = (shift?: any): ShiftPaymentIndicator => {
  if (!shift || !shift.pharmacy) {
    // Deep links and stub shifts arrive without a pharmacy. Fail safe: show nothing rather
    // than guess at how someone will be paid.
    return {
      showField: false,
      value: null,
      isRBPay: false,
      showLegacyBadge: false,
      showNote: false,
    };
  }

  if (resolveShiftNetworkPaid(shift)) {
    return {
      showField: false,
      value: ShiftPaymentMethod.NETWORK_PAID,
      isRBPay: false,
      showLegacyBadge: isLocationOnRBPay(shift.pharmacy),
      showNote: false,
    };
  }

  const snapshot = String(shift.shiftPaymentMethod || "").trim().toLowerCase();
  const isRBPay =
    snapshot === ShiftPaymentMethod.RB_PAY || snapshot === ShiftPaymentMethod.DIRECT_PAY
      ? snapshot === ShiftPaymentMethod.RB_PAY
      : isLocationOnRBPay(shift.pharmacy);

  return {
    showField: true,
    value: isRBPay ? ShiftPaymentMethod.RB_PAY : ShiftPaymentMethod.DIRECT_PAY,
    isRBPay,
    showLegacyBadge: false,
    showNote: shouldShowRBPayNotSetupNote(shift),
  };
};

export const getRBPaySetupMailTo = (email?: string | null) => {
  const recipient = email || RBAdminEmail;
  return `mailto:${recipient}?subject=${encodeURIComponent(
    RBPayPhaseZeroConfig.setupEmailSubject
  )}`;
};

// US6559 Phase 1 — RB Pay apply gate.
// A worker may apply to an RB-Pay-verified location's shift only once their own RB
// Pay onboarding is at least submitted. Statuses allowed to apply without a gating
// modal (client-confirmed): 'active' AND 'pending' — a pending worker is assumed
// likely-approved by payout time, and the RB Pay payout process re-checks that both
// worker and location are 'active' before processing.
export const RB_PAY_APPLY_ALLOWED_STATUSES: RBPayStatusValue[] = [
  RBPayStatus.ACTIVE,
  RBPayStatus.PENDING,
];

// Network-level RB Pay support contact, surfaced on the worker's account.networks[0]
// (rbPayContactEmail, then contactEmail, then the RB admin fallback). Fills
// {networkadminemail} in the "contact your network admin" gate modal.
export const getRBPayContactEmail = (account?: any) =>
  String(
    account?.networks?.[0]?.rbPayContactEmail ||
      account?.networks?.[0]?.contactEmail ||
      RBAdminEmail
  );

export type RBPayApplyGateModal = "none" | "setup" | "contactAdmin" | "onHold";

export interface RBPayApplyGateResult {
  allow: boolean;
  modal: RBPayApplyGateModal;
  contactEmail: string;
}

interface RBPayApplyGateProps {
  account?: any;
  pharmacy?: any;
  industryMode?: string | null;
  roleName?: string | null;
}

// Decide, at an Apply CTA, whether the worker may proceed or must see a gate modal.
//   allow=true,  modal='none'         -> proceed with the normal apply flow.
//   allow=false, modal='setup'        -> worker can self-serve: show "RB Pay Setup
//                                        Required" (Close + Setup RB Pay -> RBPaySettings).
//   allow=false, modal='contactAdmin' -> worker is NOT RB-Pay-enabled by their admin and
//                                        cannot reach setup: show the contact-admin modal
//                                        (ok only), with contactEmail interpolated.
// IMPORTANT — pass the SEARCH-sourced pharmacy (from the card's navigation state), NOT
// the pharmacy from the shift/:id/potential detail fetch. Only the search response
// carries the authoritative `rbPayApproved` (subquery on location_rb_pay_profiles). The
// detail endpoint returns the pharmacy's denormalized columns (rbPayStatus /
// isRbPayAvailable) which are "sticky" and NOT reset on un-approval, so they cannot be
// trusted. When no search-sourced value is available (browser refresh / deep link with
// no nav state), rbPayApproved is undefined -> gate allows; the BE payment-time safety
// check (which re-confirms worker + location are both active) is the backstop.
// Rules (US6559 Phase 1, client-confirmed):
//   0. Only locations whose own RB Pay setup is 'active' or 'pending_review' gate at all
//      (isLocationOnRBPay). On-hold / not-set-up locations never gate.
//   1. Admin has RB Pay switched OFF for this worker (pharmacist.isRbPayAvailable !==
//      true) -> 'contactAdmin'. Checked FIRST: the flag is the master control, so it
//      outranks both the status allow-list and On Hold.
//   2. Worker 'active'/'pending' (and enabled) apply normally.
//   3. Worker 'on_hold' (and enabled) -> 'onHold' — an admin must lift it.
//   4. Otherwise 'setup' when the worker can actually reach RB Pay setup
//      (isRBPayAvailableForWorker adds the industry/role checks on top of the flag, so
//      "Setup RB Pay" is never a dead-end), else 'contactAdmin'.
export const evaluateRBPayApplyGate = ({
  account,
  pharmacy,
  industryMode,
  roleName,
}: RBPayApplyGateProps): RBPayApplyGateResult => {
  if (!isLocationOnRBPay(pharmacy)) {
    return { allow: true, modal: "none", contactEmail: "" };
  }

  // The admin switch for this specific worker. Deliberately the RAW flag, not
  // isRBPayAvailableForWorker (which also folds in industry/role to answer a different
  // question: "can they reach the setup screen?"). Here we only want "has the admin
  // turned RB Pay on for this worker?".
  //
  // Checked FIRST — ahead of the status allow-list (client-confirmed). The flag is the
  // master control: RB Pay off means the worker cannot transact with an RB-Pay location
  // at all, and they cannot even open RB Pay Settings to see why (the page redirects
  // when the flag is off), so an already-'active'/'pending' profile does not earn them
  // an apply. Strict `=== true`, so a NULL / never-configured flag reads as disabled —
  // see the audit note in the US6559 QA report before release.
  if (account?.pharmacist?.isRbPayAvailable !== true) {
    return {
      allow: false,
      modal: "contactAdmin",
      contactEmail: getRBPayContactEmail(account),
    };
  }

  const status = resolveRbPayStatus(account?.pharmacist);
  if (RB_PAY_APPLY_ALLOWED_STATUSES.includes(status)) {
    return { allow: true, modal: "none", contactEmail: "" };
  }

  // On Hold (admin still has RB Pay enabled): the worker cannot self-serve — an admin
  // must lift the hold. Show the dedicated "your RB Pay account is on hold, contact the
  // Network Admin" message. This differs from Not-Started, which offers the self-serve
  // "Setup RB Pay" flow below.
  if (status === RBPayStatus.ON_HOLD) {
    return {
      allow: false,
      modal: "onHold",
      contactEmail: getRBPayContactEmail(account),
    };
  }

  // Not started. The flag is already known true here, so this only adds the industry /
  // role checks — "Setup RB Pay" must never lead to a page that redirects away.
  if (isRBPayAvailableForWorker({ industryMode, roleName, account })) {
    return { allow: false, modal: "setup", contactEmail: "" };
  }

  return {
    allow: false,
    modal: "contactAdmin",
    contactEmail: getRBPayContactEmail(account),
  };
};